import { useEffect } from "react";
import { XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePwaInstall } from "@/hooks/usePwaInstall";

/**
 * Small install hint. Android uses the browser install prompt when Chrome
 * provides one. iOS Safari has no prompt API, so the hint points at
 * Share → Add to Home Screen. Hidden once the app is standalone or dismissed.
 */
const PwaInstallPrompt = () => {
  const { platform, dismiss, install } = usePwaInstall();

  useEffect(() => {
    if (!platform) return;
    document.documentElement.setAttribute("data-pwa-install", platform);
    return () => document.documentElement.removeAttribute("data-pwa-install");
  }, [platform]);

  if (!platform) return null;

  const ios = platform === "ios";

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] pointer-events-none">
      <div
        role="region"
        aria-label={ios ? "Add to Home Screen" : "Install Task Fusion"}
        className="pointer-events-auto mx-auto flex max-w-md items-center gap-3 rounded-lg border bg-card px-3 py-2.5 shadow-lg"
      >
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium leading-tight">
            {ios ? "Add to Home Screen" : "Install Task Fusion"}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground leading-snug">
            {ios
              ? "In Safari, tap Share, then Add to Home Screen."
              : "Add it to your home screen."}
          </p>
        </div>
        {!ios && (
          <Button type="button" size="sm" className="shrink-0" onClick={() => void install()}>
            Install
          </Button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-full p-1.5 text-muted-foreground hover:text-foreground"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallPrompt;
